import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Table, Dropdown, Menu, Select } from "antd";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import DeleteModal from "../../../components/modelpopup/DeleteModal";
import AdminLeaveAddModelPopup from "../../../components/modelpopup/EmployeeLeaveModelPopup"; // Ensure the correct import
import Breadcrumbs from "../../../components/Breadcrumbs"; // Ensure the correct import
import SearchBox from "../../../components/SearchBox"; // Ensure the correct import
import LeaveFilter from "../../../components/LeaveFilter"; // Ensure the correct import

import {
  Avatar_01,
  Avatar_02,
  Avatar_03,
  Avatar_05,
  Avatar_06,
  Avatar_07,
  Avatar_08,
  Avatar_09,
  Avatar_10,
  Avatar_11,
  Avatar_12,
} from "../../../Routes/ImagePath"; // Ensure the correct import

const { Option } = Select;

const AdminLeave = () => {
  const [leaveData, setLeaveData] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const dateFormat = localStorage.getItem("dateFormat") || "MMM DD, YYYY";
  const [loading, setLoading] = useState(true);
  const [leaveToEdit, setLeaveToEdit] = useState(null);
  const [viewMode, setViewMode] = useState(false); // Initialize view mode state
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterLeaveType, setFilterLeaveType] = useState(null); // Adjust filter default
  const [isFullScreen, setFullScreen] = useState(false);

  const maximizeBtnRef = useRef(null);
  const leaveTypeSelectRef = useRef(null);

  const fetchLeaveData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/leaves/list`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setLeaveData(response.data.leaves);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching leave data:", error);
    }
  };

  const fetchLeaveTypes = async () => {
    try {
      const response = await axios.get(`${apiUrl}/leavetype/list?basic=true`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setLeaveTypes(response.data);
    } catch (error) {
      console.error("Error fetching leave types:", error);
    }
  };

  useEffect(() => {
    if (apiUrl && token) {
      fetchLeaveData();
      fetchLeaveTypes();
    }
  }, [apiUrl, token]);

  const handleEditClick = (record) => {
    setViewMode(false); // Switch to edit mode
    setLeaveToEdit(record);
  };

  const handleViewClick = (record) => {
    setViewMode(true); // Switch to view mode
    setLeaveToEdit(record);
  };

  const handleSave = (payload) => {
    setLeaveData((prevData) => {
      const leaveId = payload.leave_id;
      const updatedData = leaveId
        ? prevData.map((leave) => (leave.leave_id === leaveId ? payload : leave))
        : [...prevData, payload];
      return updatedData;
    });
  };

  const handleUpdateStatus = async (leaveId, newStatus) => {
    try {
      const response = await axios.put(
        `${apiUrl}/leaves/update-status/${leaveId}`,
        {
          status: newStatus,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const updatedLeave = response.data.leave;

      setLeaveData((prevData) =>
        prevData.map((leave) =>
          leave.leave_id === updatedLeave.leave_id ? updatedLeave : leave
        )
      );

      toast.success("Leave status has been updated.");
    } catch (error) {
      let errorMessage = "Failed to update leave status. Please try again later.";
      if (error.response && error.response.data && error.response.data.error) {
        errorMessage = error.response.data.error;
      }
      Swal.fire("Error!", errorMessage, "error");
    }
  };

  const handleDeleteClick = async (leaveId) => {
    try {
      const response = await axios.delete(`${apiUrl}/leaves/delete/${leaveId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setLeaveData((prevData) =>
        prevData.filter((leave) => leave.leave_id !== leaveId)
      );

      toast.success("Leave request has been deleted.");
    } catch (error) {
      let errorMessage = "Failed to delete leaves. Please try again later.";
      if (error.response && error.response.data && error.response.data.error) {
        errorMessage = error.response.data.error;
      }
      Swal.fire("Error!", errorMessage, "error");
    }
  };

  const handleStatusFilterChange = (value) => {
    setFilterStatus(value);
  };

  const handleLeaveTypeFilterChange = (value) => {
    setFilterLeaveType(value);
  };

  const filteredLeaveData = leaveData.filter((leave) => {
    if (filterStatus !== null && leave.leave_status !== filterStatus) {
      return false;
    }
    return true;
  });

  const columns = [
    {
      title: "Employee",
      dataIndex: "empname",
      key: "empname",
      render: (text, record) => (
        <span className="table-avatar">
          <Link to="/profile" className="avatar">
            <img alt="" src={record.image || Avatar_01} />
          </Link>
          {text}
        </span>
      ),
      sorter: (a, b) => a.empname.localeCompare(b.empname),
    },
    {
      title: "Emp Code",
      dataIndex: "empcode",
      key: "empcode",
      sorter: (a, b) => a.empcode.localeCompare(b.empcode),
    },
    {
      title: "Leave Type",
      dataIndex: "leave_type_id",
      render: (leaveTypeId) => {
        const leaveType = leaveTypes.find(
          (type) => type.leave_type_id === leaveTypeId
        );
        return leaveType ? leaveType.leavetypename : "Unknown";
      },
    },
    {
      title: "From",
      dataIndex: "leave_start_date",
      sorter: (a, b) =>
        moment(a.leave_start_date).unix() - moment(b.leave_start_date).unix(),
      render: (date) => moment(date).format(dateFormat),
    },
    {
      title: "To",
      dataIndex: "leave_end_date",
      sorter: (a, b) =>
        moment(a.leave_end_date).unix() - moment(b.leave_end_date).unix(),
      render: (date) => moment(date).format(dateFormat),
    },
    {
      title: "No of Days",
      dataIndex: "leave_duration",
      sorter: (a, b) => a.leave_duration - b.leave_duration,
    },
    {
      title: "Reason",
      dataIndex: "leave_reason",
    },
    {
      title: "Request Date",
      dataIndex: "created_at",
      sorter: (a, b) => moment(a.created_at).unix() - moment(b.created_at).unix(),
      render: (date) => moment(date).format(dateFormat),
    },
    {
      title: "Status",
      dataIndex: "leave_status",
      render: (status) => (
        <span
          className={`badge ${
            status === 1
              ? "bg-success"
              : status === 0
              ? "bg-warning"
              : status === 3
              ? "bg-danger"
              : "bg-info"
          }`}
        >
          {status === 1
            ? "Approved"
            : status === 0
            ? "Pending"
            : status === 3
            ? "Cancelled"
            : "Unknown"}
        </span>
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="edit"
                onClick={() => handleEditClick(record)}
                data-bs-toggle="modal"
                data-bs-target="#add_leave"
              >
                <Link to="#">
                  <i className="fa fa-pencil m-r-5" /> Edit
                </Link>
              </Menu.Item>
              <Menu.Item
                key="view"
                onClick={() => handleViewClick(record)}
                data-bs-toggle="modal"
                data-bs-target="#view_leave"
              >
                <span>
                  <i className="fa fa-eye m-r-5" /> View
                </span>
              </Menu.Item>
              <Menu.Item
                key="delete"
                onClick={() => handleDeleteClick(record.leave_id)}
              >
                <span>
                  <i className="fa fa-trash m-r-5" /> Delete
                </span>
              </Menu.Item>
              {record.leave_status === 0 && (
                <Menu.Item
                  key="update-status"
                  onClick={() => handleUpdateStatus(record.leave_id, 3)}
                >
                  <span>
                    <i className="fa fa-ban m-r-5" /> Cancel
                  </span>
                </Menu.Item>
              )}
            </Menu>
          }
          trigger={["click"]}
          placement="bottomRight"
        >
          <Link to="#" className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
            <i className="material-icons">more_vert</i>
          </Link>
        </Dropdown>
      ),
    },
  ];

  const handleAdd = () => {
    if (leaveTypeSelectRef.current) {
      leaveTypeSelectRef.current.reset();
    }
    setLeaveToEdit(null);
    setViewMode(false); // Assuming view mode
  };

  const statsData = [
    {
      id: 1,
      title: "Today Presents",
      value: "12 / 60",
    },
    {
      id: 2,
      title: "Planned Leaves",
      value: "8",
      subText: "Today",
    },
    {
      id: 3,
      title: "Unplanned Leaves",
      value: "0",
      subText: "Today",
    },
    {
      id: 4,
      title: "Pending Requests",
      value: "12",
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
        <div className="page-header page-top-icons">

          <div className="row">
            <div className="col-md-4">
              <h3 className="page-title">Leave Applications</h3>
            </div>
            <div className="col-md-4">
              <div className="d-flex align-items-center">
                <Select
                  defaultValue={null}
                  placeholder="Filter by Status"
                  style={{ width: 160, marginRight: 10 }}
                  onChange={handleStatusFilterChange}
                >
                  <Option value={null}>All Statuses</Option>
                  <Option value={1}>Approved</Option>
                  <Option value={0}>Pending</Option>
                  <Option value={3}>Cancelled</Option>
                </Select>
                <Select
                  defaultValue="All"
                  style={{ width: 160 }}
                  onChange={handleLeaveTypeFilterChange}
                  ref={leaveTypeSelectRef} // Assign ref here
                >
                  <Option value="All">All Leave Types</Option>
                  <Option value="My Leave">My Leave</Option>
                  <Option value="My Team Leave">My Team Leave</Option>
                </Select>
              </div>
            </div>
            <div className="col-md-4 float-end ms-auto">
              <div className="d-flex title-head">
                <div className="view-icons">
                  <Link to="#" className="grid-view btn btn-link">
                    <i className="las la-redo-alt" />
                  </Link>
                  <Link to="#" className="list-view btn btn-link" id="collapse-header" ref={maximizeBtnRef}>
                    <i className="las la-expand-arrows-alt" />
                  </Link>
                </div>
                <Link to="#" className="btn add-btn" data-bs-toggle="modal" data-bs-target="#add_leave" onClick={handleAdd}>
                  <i className="la la-plus-circle" /> Apply New
                </Link>
              </div>
            </div>
          </div>
        </div> 
          <div className="row">
            {statsData.map((stat) => (
              <div className="col-md-3 d-flex" key={stat.id}>
                <div className="stats-info w-100">
                  <h6>{stat.title}</h6>
                  {stat.subText ? (
                    <h4>
                      {stat.value} <span>{stat.subText}</span>
                    </h4>
                  ) : (
                    <h4>{stat.value}</h4>
                  )}
                </div>
              </div>
            ))}
          </div>
         

          <div className="row">
            <div className="col-md-12">
              {loading ? (
                <div id="loader-wrapper-">
                  <div className="loader">
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                  </div>
                </div>
              ) : (
                <div className="table-responsive">
                  <SearchBox />
                  <Table
                    columns={columns}
                    dataSource={filteredLeaveData}
                    rowKey="leave_id"
                    className="table-striped"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <AdminLeaveAddModelPopup onSave={handleSave} leaveData={leaveToEdit} viewMode={viewMode} />
      <div className="sidebar-overlay" data-reff="#sidebar"></div>
    </>
  );
};

export default AdminLeave;
